import React, { useState, useEffect, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';
import {
  FiArrowLeft,
  FiArrowRight,
  FiCheck,
  FiX,
  FiRotateCw,
  FiSend,
} from 'react-icons/fi';
import api from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';
import LoadingSpinner from '../common/LoadingSpinner';
import { BRAND, GOLD_GRADIENT, IVORY_GRADIENT_TEXT, Page, Card, Tag, ProgressBar, Stat, Kicker, Serif, Button, Alert, buttonStyle, fadeUp, stagger } from '../common/ui';

const slideVariants = {
  enter: (dir) => ({ x: dir > 0 ? 80 : -80, opacity: 0 }),
  center: { x: 0, opacity: 1 },
  exit: (dir) => ({ x: dir < 0 ? 80 : -80, opacity: 0 }),
};

const letter = (i) => String.fromCharCode(65 + i);

// Letter chip (A/B/C/D) shown at the left of every answer option.
// tone: 'idle' (gold outline) | 'selected' (navy on gold) | 'success' | 'danger'
function LetterChip({ index, tone = 'idle' }) {
  const t = {
    idle: { bg: 'transparent', bd: BRAND.gold, fg: BRAND.goldLight },
    selected: { bg: BRAND.navy900, bd: BRAND.navy900, fg: BRAND.goldLight },
    success: { bg: BRAND.success, bd: BRAND.success, fg: BRAND.navy900 },
    danger: { bg: BRAND.danger, bd: BRAND.danger, fg: BRAND.navy900 },
  }[tone] || { bg: 'transparent', bd: BRAND.gold, fg: BRAND.goldLight };
  return (
    <span style={{ width: 34, height: 34, borderRadius: '50%', flexShrink: 0, display: 'inline-grid', placeItems: 'center', fontWeight: 900, fontSize: '0.9rem', background: t.bg, border: `2px solid ${t.bd}`, color: t.fg, transition: 'all .18s' }}>
      {letter(index)}
    </span>
  );
}

export default function QuizPlay() {
  const { id } = useParams();
  const { user } = useAuth();
  const quizBase = user?.role === 'admin' ? '/admin/quiz' : '/quiz';
  const [quiz, setQuiz] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [phase, setPhase] = useState('play');
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [slideDir, setSlideDir] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const loadQuiz = useCallback(async () => {
    setLoading(true);
    setFetchError(null);
    setPhase('play');
    setCurrentIndex(0);
    setAnswers([]);
    setResult(null);
    try {
      const { data } = await api.get(`/quizzes/${id}`);
      const q = data?.quiz;
      if (!q) throw new Error('Quiz not found');
      setQuiz(q);
      setAnswers(Array((q.questions || []).length).fill(null));
    } catch (e) {
      const msg =
        e.response?.data?.error ||
        e.message ||
        "We couldn't load the quiz. Try again!";
      setFetchError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadQuiz();
  }, [loadQuiz]);

  const questions = quiz?.questions || [];
  const total = questions.length;
  const current = questions[currentIndex];
  const selected = answers[currentIndex];
  const progressPct = total ? Math.round(((currentIndex + 1) / total) * 100) : 0;

  const selectOption = (optionIndex) => {
    setAnswers((prev) => {
      const next = [...prev];
      next[currentIndex] = optionIndex;
      return next;
    });
  };

  const goNext = () => {
    if (selected === null || selected === undefined) {
      toast.error('Choose an answer before continuing!');
      return;
    }
    if (currentIndex < total - 1) {
      setSlideDir(1);
      setCurrentIndex((i) => i + 1);
    }
  };

  const submitQuiz = async () => {
    if (selected === null || selected === undefined) {
      toast.error('Choose an answer before submitting!');
      return;
    }
    setSubmitting(true);
    try {
      const payload = questions.map((_, idx) =>
        answers[idx] !== null && answers[idx] !== undefined ? answers[idx] : -1
      );
      const { data } = await api.post(`/quizzes/${id}/submit`, { answers: payload });
      setResult(data);
      setPhase('results');
      toast.success(data.passed ? 'You passed the quiz! 🎉' : 'Quiz complete!');
    } catch (e) {
      const msg =
        e.response?.data?.error || "Couldn't submit. Check your connection and try again.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const restart = () => {
    setSlideDir(1);
    setPhase('play');
    setCurrentIndex(0);
    setAnswers(Array(total).fill(null));
    setResult(null);
  };

  // Backdrop = the lesson's own cinematic chapter covers (lessonN/scenes/cover-c1..c3.jpg):
  // the scene advances with the quiz (first third → c1, second → c2, last → c3) and the
  // results page shows the closing cover. Lesson 1 has a single cover, so it reuses its scenes.
  const lessonNo = Number.isInteger(quiz?.order) ? quiz.order + 1 : null;
  const chapter = total ? Math.min(3, Math.floor((currentIndex / total) * 3) + 1) : 1;
  const heroImg = !lessonNo
    ? '/illustrations/lesson1/scenes/challenge.jpg'
    : lessonNo === 1
      ? ['/illustrations/lesson1/scenes/cover-c1.jpg', '/illustrations/lesson1/scenes/challenge-c3.jpg', '/illustrations/lesson1/scenes/challenge-c5.jpg'][phase === 'results' ? 2 : chapter - 1]
      : `/illustrations/lesson${lessonNo}/scenes/cover-c${phase === 'results' ? 3 : chapter}.jpg`;
  const pageProps = { hero: { img: heroImg, pos: 'center 30%', height: 720, opacity: 0.68 }, maxWidth: 840 };

  if (loading) {
    return (
      <Page {...pageProps}>
        <LoadingSpinner message="Preparing the quiz…" />
      </Page>
    );
  }

  if (fetchError || !quiz) {
    return (
      <Page {...pageProps}>
        <Card style={{ textAlign: 'center', padding: '36px 28px' }}>
          <div style={{ fontSize: '2.4rem', marginBottom: 10 }}>🦉</div>
          <Alert style={{ display: 'inline-block', marginBottom: 20 }}>{fetchError || 'Quiz not available.'}</Alert>
          <div>
            <Button to={quizBase} variant="gold"><FiArrowLeft /> Back to quizzes</Button>
          </div>
        </Card>
      </Page>
    );
  }

  if (phase === 'results' && result) {
    const passed = result.passed;
    const results = result.results || [];
    const correctCount = results.filter((r) => r.correct).length;
    return (
      <Page {...pageProps}>
        {/* score hero */}
        <motion.div variants={fadeUp} initial="hidden" animate="visible">
          <Card style={{ textAlign: 'center', padding: 'clamp(28px,4vw,44px) 24px 30px', position: 'relative', overflow: 'hidden' }}>
            <div aria-hidden style={{ position: 'absolute', inset: 0, background: `radial-gradient(60% 70% at 50% 0%, ${passed ? 'rgba(95,191,138,0.18)' : 'rgba(205,155,80,0.16)'} 0%, transparent 70%)`, pointerEvents: 'none' }} />
            <div style={{ position: 'relative' }}>
              <Kicker>Mission report</Kicker>
              <h1 style={{ fontSize: 'clamp(3.6rem, 12vw, 5.6rem)', fontWeight: 900, lineHeight: 1, margin: '12px 0 8px', ...IVORY_GRADIENT_TEXT }}>
                {result.percentage}%
              </h1>
              <div style={{ marginBottom: 12 }}>
                {passed
                  ? <Tag tone="success"><FiCheck size={12} /> Passed</Tag>
                  : <Tag tone="danger"><FiX size={12} /> Not passed yet</Tag>}
              </div>
              <p style={{ fontSize: 'clamp(1.2rem,2.4vw,1.5rem)', fontWeight: 900, margin: '0 0 6px' }}>
                {passed ? 'Great work — you passed the quiz!' : 'Almost there! Review and try again.'}
              </p>
              <Serif style={{ fontSize: '1.15rem' }}>
                {passed ? 'Trace is proud. One more skill in your defender toolkit.' : 'Every expert got a few wrong at first. Check the answers below.'}
              </Serif>
            </div>
          </Card>
        </motion.div>

        {/* stat tiles */}
        <motion.div variants={stagger} initial="hidden" animate="visible" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 14, marginTop: 16 }}>
          <motion.div variants={fadeUp}>
            <Stat
              icon="⭐"
              value={result.preview ? `${result.score} / ${result.maxScore}` : `+${result.pointsEarned ?? (passed ? result.score : 0)}`}
              label={result.preview ? 'score (preview)' : (result.pointsEarned ?? 1) > 0 ? 'points earned' : passed ? 'points — best already saved' : 'points — pass to earn'}
            />
          </motion.div>
          <motion.div variants={fadeUp}><Stat icon="✅" value={`${correctCount} / ${results.length || total}`} label="correct answers" /></motion.div>
          <motion.div variants={fadeUp}><Stat icon="🎯" value={`${quiz.passingScore}%`} label="passing score" /></motion.div>
        </motion.div>

        {/* review */}
        <div style={{ marginTop: 28, marginBottom: 12 }}>
          <Kicker>Review</Kicker>
          <h2 style={{ fontSize: '1.3rem', fontWeight: 900, margin: '6px 0 0' }}>How you did on each question</h2>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginBottom: 28 }}>
          {results.map((r, i) => {
            const q = questions[r.questionIndex] || questions[i];
            const opts = q?.options || [];
            const picked = opts[r.selectedIndex]?.text;
            const correct = opts[r.correctIndex]?.text;
            return (
              <motion.div
                key={`${r.questionIndex}-${i}`}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }}
              >
                <Card style={{ padding: '18px 20px', borderLeft: `4px solid ${r.correct ? BRAND.success : BRAND.danger}` }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
                    <Tag tone="muted">Question {(r.questionIndex ?? i) + 1}</Tag>
                    {r.correct
                      ? <Tag tone="success"><FiCheck size={12} /> Correct</Tag>
                      : <Tag tone="danger"><FiX size={12} /> Incorrect</Tag>}
                  </div>
                  <p style={{ fontWeight: 800, fontSize: '1.08rem', margin: '0 0 12px', lineHeight: 1.4 }}>
                    {q?.text || `Question ${i + 1}`}
                  </p>

                  {opts.length > 0 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {opts.map((opt, oi) => {
                        const isCorrect = oi === r.correctIndex;
                        const isPicked = oi === r.selectedIndex;
                        const tone = isCorrect ? 'success' : isPicked ? 'danger' : 'idle';
                        const bd = isCorrect ? 'rgba(95,191,138,0.6)' : isPicked ? 'rgba(224,122,122,0.6)' : BRAND.lineSoft;
                        const bg = isCorrect ? 'rgba(95,191,138,0.12)' : isPicked ? 'rgba(224,122,122,0.12)' : 'rgba(7,20,45,0.45)';
                        return (
                          <div key={oi} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '9px 12px', borderRadius: 14, background: bg, border: `1px solid ${bd}`, fontSize: '0.95rem', fontWeight: isCorrect || isPicked ? 800 : 600, color: isCorrect || isPicked ? BRAND.ivory : BRAND.muted }}>
                            <LetterChip index={oi} tone={tone} />
                            <span style={{ flex: 1 }}>{opt.text}</span>
                            {isCorrect && <Tag tone="success">Answer</Tag>}
                            {isPicked && !isCorrect && <Tag tone="danger">Yours</Tag>}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <>
                      <p style={{ fontSize: '0.95rem', color: BRAND.muted, margin: 0 }}>
                        Your answer: <strong style={{ color: BRAND.ivory }}>{picked || '(none)'}</strong>
                      </p>
                      {!r.correct && (
                        <p style={{ fontSize: '0.95rem', marginTop: 6, color: BRAND.muted }}>
                          Correct answer: <strong style={{ color: '#8fd9ad' }}>{correct}</strong>
                        </p>
                      )}
                    </>
                  )}

                  {r.explanation ? (
                    <div style={{ marginTop: 14, padding: '12px 14px', borderRadius: 14, background: 'rgba(205,155,80,0.1)', border: `1px solid ${BRAND.line}`, display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                      <span style={{ fontSize: '1.1rem', lineHeight: 1.3 }}>💡</span>
                      <p style={{ margin: 0, fontSize: '0.95rem', lineHeight: 1.5, color: 'rgba(249,240,231,0.9)' }}>{r.explanation}</p>
                    </div>
                  ) : null}
                </Card>
              </motion.div>
            );
          })}
        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, justifyContent: 'center' }}>
          <Button to={quizBase} variant="gold" size="lg"><FiArrowLeft /> Back to quizzes</Button>
          <Button variant="outline" size="lg" onClick={restart}><FiRotateCw /> Try again</Button>
        </div>
      </Page>
    );
  }

  const lastQuestion = currentIndex >= total - 1;

  return (
    <Page {...pageProps}>
      <style>{`
        .core-opt { transition: border-color .18s, background .18s, transform .18s, box-shadow .18s; }
        .core-opt:hover, .core-opt:focus-visible { border-color: ${BRAND.gold} !important; outline: none; box-shadow: 0 0 0 3px rgba(205,155,80,0.22); }
        @media (max-width: 560px) { .core-quiz-top { flex-direction: column; align-items: flex-start !important; } }
      `}</style>

      {/* top bar */}
      <motion.div variants={fadeUp} initial="hidden" animate="visible">
        <Card style={{ padding: '16px 20px', marginBottom: 18 }}>
          <div className="core-quiz-top" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
            <div style={{ minWidth: 0 }}>
              <Kicker style={{ fontSize: '0.68rem' }}>Quiz</Kicker>
              <h1 style={{ fontSize: 'clamp(1.1rem,2.4vw,1.35rem)', fontWeight: 900, margin: '4px 0 0', lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis' }}>{quiz.title}</h1>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <Tag tone="gold">Question {currentIndex + 1} of {total}</Tag>
              <span style={{ fontWeight: 900, color: BRAND.goldLight, fontSize: '0.9rem' }}>{progressPct}%</span>
            </div>
          </div>
          <ProgressBar value={progressPct} height={6} />
        </Card>
      </motion.div>

      <AnimatePresence mode="wait" custom={slideDir}>
        <motion.div
          key={currentIndex}
          custom={slideDir}
          variants={slideVariants}
          initial="enter"
          animate="center"
          exit="exit"
          transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
        >
          {/* question */}
          <Card style={{ padding: 'clamp(20px,3vw,28px) clamp(20px,3vw,30px)', marginBottom: 18 }}>
            <Kicker style={{ fontSize: '0.68rem', marginBottom: 8 }}>Question {currentIndex + 1}</Kicker>
            <h2
              style={{
                fontFamily: BRAND.font,
                fontSize: 'clamp(1.3rem, 2.6vw, 1.6rem)',
                fontWeight: 800,
                lineHeight: 1.35,
                margin: 0,
                color: BRAND.ivory,
              }}
            >
              {current?.text}
            </h2>
          </Card>

          {/* options */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 24 }}>
            {(current?.options || []).map((opt, optIdx) => {
              const isSel = selected === optIdx;
              return (
                <motion.button
                  key={optIdx}
                  type="button"
                  className="core-opt"
                  aria-pressed={isSel}
                  whileHover={{ x: 4 }}
                  whileTap={{ scale: 0.985 }}
                  onClick={() => selectOption(optIdx)}
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 14,
                    padding: '14px 16px',
                    minHeight: 64,
                    borderRadius: 18,
                    textAlign: 'left',
                    fontFamily: BRAND.font,
                    fontSize: '1.05rem',
                    fontWeight: isSel ? 800 : 600,
                    lineHeight: 1.35,
                    cursor: 'pointer',
                    background: isSel ? GOLD_GRADIENT : 'rgba(11,26,58,0.72)',
                    color: isSel ? BRAND.navy900 : BRAND.ivory,
                    border: `1px solid ${isSel ? BRAND.gold : BRAND.line}`,
                    boxShadow: isSel ? '0 10px 30px rgba(205,155,80,0.35)' : '0 10px 26px rgba(0,0,0,0.28)',
                    backdropFilter: 'blur(10px)',
                    WebkitBackdropFilter: 'blur(10px)',
                  }}
                >
                  <LetterChip index={optIdx} tone={isSel ? 'selected' : 'idle'} />
                  <span style={{ flex: 1 }}>{opt.text}</span>
                  {isSel && <FiCheck size={20} style={{ flexShrink: 0 }} />}
                </motion.button>
              );
            })}
          </div>
        </motion.div>
      </AnimatePresence>

      {/* nav */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, justifyContent: 'space-between', alignItems: 'center' }}>
        <Link to={quizBase} style={buttonStyle('ghost', 'md')}>
          <FiArrowLeft /> Exit
        </Link>
        {!lastQuestion ? (
          <Button variant="gold" size="lg" onClick={goNext}>
            Next <FiArrowRight size={18} />
          </Button>
        ) : (
          <Button
            variant="gold"
            size="lg"
            disabled={submitting}
            onClick={submitQuiz}
            style={{ cursor: submitting ? 'wait' : undefined }}
          >
            {submitting ? 'Submitting…' : 'Submit answers'} <FiSend size={16} />
          </Button>
        )}
      </div>
    </Page>
  );
}
